import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Filter, RefreshCw, Settings2 } from 'lucide-react';
import { ComplaintActionModal } from '@/components/complaints/complaint-action-modal';
import { ComplaintConfigPanel } from '@/components/complaints/complaint-config-panel';
import { ComplaintDetailModal } from '@/components/complaints/complaint-detail-modal';
import { Modal } from '@/components/ui/modal';
import { ApiError } from '@/lib/api';
import {
  exportComplaintWorkbenchApi,
  getComplaintMetaApi,
  getComplaintWorkbenchDetailApi,
  listComplaintWorkbenchApi,
  type ComplaintCategory,
  type ComplaintConclusion,
  type ComplaintDetail,
  type ComplaintListItem,
  type ComplaintStatus,
} from '@/lib/complaints-api';
import {
  buildComplaintWorkbenchQuery,
  COMPLAINT_CATEGORY_LABELS,
  COMPLAINT_CONCLUSION_LABELS,
  COMPLAINT_STATUS_LABELS,
  DEFAULT_COMPLAINT_FILTERS,
  formatComplaintDate,
  maskEmployeeId,
  type ComplaintActionMode,
  type ComplaintFilterDraft,
} from '@/lib/complaints-model';
import { triggerBrowserDownload } from '@/lib/download';

const INPUT =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PAGE_SIZE = 20;

export function ComplaintsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'workbench' | 'config'>('workbench');
  const [draftFilters, setDraftFilters] = useState<ComplaintFilterDraft>({
    ...DEFAULT_COMPLAINT_FILTERS,
  });
  const [appliedFilters, setAppliedFilters] = useState<ComplaintFilterDraft>({
    ...DEFAULT_COMPLAINT_FILTERS,
  });
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionMode, setActionMode] = useState<ComplaintActionMode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const metaQuery = useQuery({
    queryKey: ['complaints', 'meta'],
    queryFn: getComplaintMetaApi,
  });

  const listQueryParams = useMemo(
    () => buildComplaintWorkbenchQuery(appliedFilters, page, PAGE_SIZE),
    [appliedFilters, page],
  );
  const listQuery = useQuery({
    queryKey: ['complaints', 'workbench-list', listQueryParams],
    queryFn: () => listComplaintWorkbenchApi(listQueryParams),
    enabled: !!metaQuery.data?.capabilities.canHandle,
  });
  const detailQuery = useQuery({
    queryKey: ['complaints', 'detail', selectedId],
    queryFn: () => getComplaintWorkbenchDetailApi(selectedId ?? ''),
    enabled: !!selectedId,
  });

  const capabilities = metaQuery.data?.capabilities;
  const availableCategories = capabilities?.handleCategories ?? [];

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...draftFilters });
    setNotice(null);
  }

  function resetFilters() {
    const next = { ...DEFAULT_COMPLAINT_FILTERS };
    setDraftFilters(next);
    setAppliedFilters(next);
    setPage(1);
    setNotice(null);
  }

  async function exportCurrentFilters() {
    setExporting(true);
    setNotice(null);
    try {
      const file = await exportComplaintWorkbenchApi(listQueryParams);
      triggerBrowserDownload(file.blob, file.fileName);
      setNotice('已从后端导出当前筛选的全部结果。CSV 内容未在前端拼装。');
    } catch (error) {
      setNotice(toPageError(error));
    } finally {
      setExporting(false);
    }
  }

  async function handleConflict() {
    setActionMode(null);
    setNotice('记录已被其他处理人更新');
    await detailQuery.refetch();
    await queryClient.invalidateQueries({
      queryKey: ['complaints', 'workbench-list'],
    });
  }

  function handleUpdated(next: ComplaintDetail) {
    queryClient.setQueryData(['complaints', 'detail', next.id], next);
    setActionMode(null);
    setNotice(`投诉 ${next.complaintNo} 已按服务端状态更新至 version ${next.version}。`);
    void queryClient.invalidateQueries({
      queryKey: ['complaints', 'workbench-list'],
    });
  }

  if (metaQuery.isLoading) {
    return <p className="text-sm text-slate-500">正在加载投诉工作台权限…</p>;
  }
  if (metaQuery.isError) {
    return <PageError error={metaQuery.error} />;
  }
  if (!capabilities?.canHandle) {
    return (
      <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
        当前账号没有服务端授予的投诉处理能力。
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="complaint-workbench-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">投诉处理工作台</h1>
          <p className="mt-1 text-sm text-slate-500">
            权限由服务端按 orgId、employeeId 与类别配置计算；前端入口不构成授权。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTab('workbench')}
            className={tab === 'workbench' ? ACTIVE_TAB : INACTIVE_TAB}
          >
            处理台
          </button>
          {capabilities.canConfigureHandlers ? (
            <button
              type="button"
              onClick={() => setTab('config')}
              className={tab === 'config' ? ACTIVE_TAB : INACTIVE_TAB}
              data-testid="complaint-config-tab"
            >
              <Settings2 className="h-4 w-4" /> 配置
            </button>
          ) : null}
        </div>
      </header>

      {notice ? (
        <div role="status" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          {notice}
        </div>
      ) : null}

      {tab === 'config' && capabilities.canConfigureHandlers ? (
        <ComplaintConfigPanel />
      ) : (
        <>
          <form
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            onSubmit={applyFilters}
          >
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Filter className="h-4 w-4" /> 高级搜索与排序
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Field label="投诉单号 / 标题关键词">
                <input
                  className={INPUT}
                  value={draftFilters.keyword}
                  maxLength={100}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      keyword: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="分类">
                <select
                  className={INPUT}
                  value={draftFilters.category}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      category: event.target.value as ComplaintCategory | '',
                    }))
                  }
                >
                  <option value="">全部授权分类</option>
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {COMPLAINT_CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="状态">
                <select
                  className={INPUT}
                  value={draftFilters.status}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      status: event.target.value as ComplaintStatus | '',
                    }))
                  }
                >
                  <option value="">全部状态</option>
                  {(Object.keys(COMPLAINT_STATUS_LABELS) as ComplaintStatus[]).map(
                    (status) => (
                      <option key={status} value={status}>
                        {COMPLAINT_STATUS_LABELS[status]}
                      </option>
                    ),
                  )}
                </select>
              </Field>
              <Field label="结论">
                <select
                  className={INPUT}
                  value={draftFilters.conclusion}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      conclusion: event.target.value as ComplaintConclusion | '',
                    }))
                  }
                >
                  <option value="">全部结论</option>
                  {(Object.keys(COMPLAINT_CONCLUSION_LABELS) as ComplaintConclusion[]).map(
                    (conclusion) => (
                      <option key={conclusion} value={conclusion}>
                        {COMPLAINT_CONCLUSION_LABELS[conclusion]}
                      </option>
                    ),
                  )}
                </select>
              </Field>
              <Field label="提交人 employeeId（精确）">
                <input
                  className={INPUT}
                  value={draftFilters.submitterEmployeeId}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      submitterEmployeeId: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="处理人 employeeId（精确）">
                <input
                  className={INPUT}
                  value={draftFilters.handlerEmployeeId}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      handlerEmployeeId: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="提交开始日期">
                <input
                  type="date"
                  className={INPUT}
                  value={draftFilters.startDate}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      startDate: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="提交结束日期">
                <input
                  type="date"
                  className={INPUT}
                  value={draftFilters.endDate}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      endDate: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="排序字段">
                <select
                  className={INPUT}
                  value={draftFilters.sortBy}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      sortBy: event.target.value as ComplaintFilterDraft['sortBy'],
                    }))
                  }
                >
                  <option value="createdAt">提交时间</option>
                  <option value="updatedAt">更新时间</option>
                  <option value="dueAt">截止时间</option>
                </select>
              </Field>
              <Field label="排序方向">
                <select
                  className={INPUT}
                  value={draftFilters.sortOrder}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      sortOrder: event.target.value as ComplaintFilterDraft['sortOrder'],
                    }))
                  }
                >
                  <option value="desc">降序</option>
                  <option value="asc">升序</option>
                </select>
              </Field>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="submit" className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
                应用筛选
              </button>
              <button type="button" onClick={resetFilters} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                重置
              </button>
              <button
                type="button"
                onClick={() => void listQuery.refetch()}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className="h-4 w-4" /> 刷新
              </button>
              {capabilities.canExport ? (
                <button
                  type="button"
                  onClick={() => void exportCurrentFilters()}
                  disabled={exporting}
                  className="inline-flex items-center gap-1 rounded-lg border border-brand-300 px-4 py-2 text-sm text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                  data-testid="complaint-export-current-filters"
                >
                  <Download className="h-4 w-4" />
                  {exporting ? '导出中…' : '导出当前筛选全部结果'}
                </button>
              ) : null}
            </div>
          </form>

          <ComplaintTable
            loading={listQuery.isLoading}
            error={listQuery.error}
            items={listQuery.data?.items ?? []}
            onOpen={setSelectedId}
          />

          {listQuery.data ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
              <span>
                第 {listQuery.data.pagination.page} / {Math.max(1, listQuery.data.pagination.totalPages)} 页 · 共{' '}
                {listQuery.data.pagination.total} 条
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                >
                  上一页
                </button>
                <button
                  type="button"
                  disabled={page >= listQuery.data.pagination.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                >
                  下一页
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {selectedId && detailQuery.isLoading ? (
        <Modal key={`complaint-loading-${selectedId}`} title="正在加载投诉详情" onClose={() => setSelectedId(null)}>
          <p className="text-sm text-slate-500">请稍候…</p>
        </Modal>
      ) : null}
      {selectedId && detailQuery.isError ? (
        <Modal key={`complaint-error-${selectedId}`} title="投诉详情加载失败" onClose={() => setSelectedId(null)}>
          <p role="alert" className="text-sm text-rose-700">{toPageError(detailQuery.error)}</p>
        </Modal>
      ) : null}
      {detailQuery.data ? (
        <ComplaintDetailModal
          key={`complaint-detail-${detailQuery.data.id}-${detailQuery.data.version}`}
          detail={detailQuery.data}
          onClose={() => {
            setSelectedId(null);
            setActionMode(null);
          }}
          onOpenAction={setActionMode}
        />
      ) : null}
      {detailQuery.data && actionMode ? (
        <ComplaintActionModal
          key={`${actionMode}-${detailQuery.data.id}-${detailQuery.data.version}`}
          mode={actionMode}
          detail={detailQuery.data}
          onClose={() => setActionMode(null)}
          onUpdated={handleUpdated}
          onConflict={handleConflict}
        />
      ) : null}
    </div>
  );
}

const ACTIVE_TAB =
  'inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white';
const INACTIVE_TAB =
  'inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-xs text-slate-600">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function ComplaintTable({
  loading,
  error,
  items,
  onOpen,
}: {
  loading: boolean;
  error: unknown;
  items: ComplaintListItem[];
  onOpen: (id: string) => void;
}) {
  if (loading) return <p className="text-sm text-slate-500">正在加载投诉列表…</p>;
  if (error) return <PageError error={error} />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs text-slate-500">
          <tr>
            {['投诉单号 / 标题', '提交人', '分类', '状态 / 结论', '处理人', '提交 / 截止', '操作'].map((label) => (
              <th key={label} className="whitespace-nowrap px-4 py-3 font-medium">{label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <td className="max-w-72 px-4 py-3">
                <div className="font-mono text-xs text-slate-500">{item.complaintNo}</div>
                <div className="mt-1 truncate font-medium text-slate-900">{item.title}</div>
              </td>
              <td className="px-4 py-3 font-mono text-xs">{maskEmployeeId(item.submitterEmployeeId)}</td>
              <td className="px-4 py-3">{COMPLAINT_CATEGORY_LABELS[item.category]}<div className="text-xs text-slate-500">{item.subcategory || '—'}</div></td>
              <td className="px-4 py-3"><StatusPill status={item.status} /><div className="mt-1 text-xs text-slate-500">{item.conclusion ? COMPLAINT_CONCLUSION_LABELS[item.conclusion] : '—'} · v{item.version}</div></td>
              <td className="px-4 py-3 font-mono text-xs">{maskEmployeeId(item.currentHandlerEmployeeId)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">{formatComplaintDate(item.createdAt)}<div>{formatComplaintDate(item.dueAt)}</div></td>
              <td className="px-4 py-3"><button type="button" onClick={() => onOpen(item.id)} className="text-brand-600 hover:underline">查看详情</button></td>
            </tr>
          ))}
          {items.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">当前筛选无投诉记录</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: ComplaintStatus }) {
  return (
    <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
      {COMPLAINT_STATUS_LABELS[status]}
    </span>
  );
}

function PageError({ error }: { error: unknown }) {
  return (
    <div role="alert" className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700">
      {toPageError(error)}
    </div>
  );
}

function toPageError(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.code ? ` · ${String(error.code)}` : '';
    return error.status > 0
      ? `${error.message}（HTTP ${error.status}${code}）`
      : error.message;
  }
  return error instanceof Error ? error.message : '投诉工作台请求失败。';
}
